import React from 'react';
import { useTimezone } from '../contexts/useTimezone';
import { TIMEZONE_OPTIONS } from '../contexts/timezoneOptions';

interface TimezoneSelectorProps {
  className?: string;
  showLabel?: boolean;
  label?: string;
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  className = '',
  showLabel = true,
  label = 'Timezone',
}) => {
  const { timezone, setTimezone } = useTimezone();

  // Group timezones by region
  const groupedTimezones = TIMEZONE_OPTIONS.reduce(
    (acc, tz) => {
      if (!acc[tz.group]) {
        acc[tz.group] = [];
      }
      acc[tz.group].push(tz);
      return acc;
    },
    {} as Record<string, typeof TIMEZONE_OPTIONS>,
  );

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTimezone(event.target.value);
  };

  return (
    <div className={className}>
      {showLabel && (
        <label
          htmlFor="timezone-selector"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {label}
        </label>
      )}
      <select
        id="timezone-selector"
        value={timezone}
        onChange={handleChange}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {Object.entries(groupedTimezones).map(([group, timezones]) => (
          <optgroup key={group} label={group}>
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Current timezone: {timezone}
        {timezone !== 'UTC' && (
          <span className="ml-2">
            (UTC
            {
              new Date()
                .toLocaleString('en-US', {
                  timeZone: timezone,
                  timeZoneName: 'short',
                })
                .match(/[+-]\d{2}:\d{2}|[A-Z]{2,4}$/)?.[0]
            }
            )
          </span>
        )}
      </p>
    </div>
  );
};

export default TimezoneSelector;
